import { ref } from 'vue';
import APIResponseHandler, { RETRY_RESPONSE_SYMBOL } from '@/services/APIResponseHandler';
import { getAppEnvironment } from '@/services/ApiClient/utils';

export const isBusy = ref(false);

/**
 * Custom fetch instance for Orval-generated API client
 *
 * Authentication: Uses cookie-based JWT (jwt_hp, jwt_s, refresh_token)
 * - No Authorization header needed
 * - credentials: 'include' ensures cookies are sent automatically
 * - Refresh token stored in localStorage, managed by useUsersStore
 *
 * Features:
 * - Global isBusy state management
 * - Automatic request/response handling
 *
 * See: ADR-0401 OpenAPI Set-Cookie Headers Documentation
 */
export const customFetch = async <T>(
  url: string,
  options: RequestInit = {},
): Promise<T> => {
  const { VUE_API_BASEPATH } = getAppEnvironment();

  if (!VUE_API_BASEPATH) {
    throw new Error('The field `VUE_API_BASEPATH` is missing in the environment configuration');
  }

  const fullUrl = new URL(url, VUE_API_BASEPATH).href;

  const existingHeaders = options.headers instanceof Headers
    ? Object.fromEntries(options.headers.entries())
    : (options.headers || {});

  const headers: Record<string, string> = {
    ...(existingHeaders as Record<string, string>),
  };

  const requestInit: RequestInit = {
    ...options,
    headers,
    credentials: 'include',
  };

  isBusy.value = true;

  try {
    const response = await fetch(fullUrl, requestInit);

    const fetchArgs = {
      endpoint: fullUrl,
      reqInit: requestInit,
      handlerProps: undefined,
    };

    const data = await APIResponseHandler<unknown>(response, fetchArgs);
    const actualResponse = (fetchArgs as { [RETRY_RESPONSE_SYMBOL]?: Response })[RETRY_RESPONSE_SYMBOL] || response;

    return {
      status: actualResponse.status,
      data,
      headers: actualResponse.headers,
    } as T;
  } finally {
    isBusy.value = false;
  }
};

export default customFetch;
